import React, { useEffect, useState } from 'react';
import { Button } from './ui/button';
import { Download, X, Smartphone } from 'lucide-react';

/**
 * PWAInstallButton
 * - Listens for beforeinstallprompt (Android / desktop Chrome).
 * - On iOS Safari there's no prompt API, so we show manual instructions.
 * - Shows a floating badge; users can dismiss and it stays dismissed for 7 days.
 */
const DISMISS_KEY = 'pwa-install-dismissed-until';

const PWAInstallButton = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [visible, setVisible] = useState(false);
  const [showIosSheet, setShowIosSheet] = useState(false);

  const isStandalone =
    typeof window !== 'undefined' &&
    (window.matchMedia('(display-mode: standalone)').matches ||
      window.navigator.standalone === true);

  const isIos = () =>
    /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;

  useEffect(() => {
    if (isStandalone) return;

    const dismissedUntil = parseInt(localStorage.getItem(DISMISS_KEY) || '0', 10);
    if (dismissedUntil > Date.now()) return;

    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setVisible(true);
    };
    window.addEventListener('beforeinstallprompt', handler);

    // iOS Safari fallback — no beforeinstallprompt
    if (isIos()) {
      setTimeout(() => setVisible(true), 4000);
    }

    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, [isStandalone]);

  const dismiss = () => {
    setVisible(false);
    localStorage.setItem(DISMISS_KEY, String(Date.now() + 7 * 24 * 60 * 60 * 1000));
  };

  const install = async () => {
    if (isIos()) {
      setShowIosSheet(true);
      return;
    }
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === 'accepted') dismiss();
    setDeferredPrompt(null);
  };

  if (isStandalone || !visible) return null;

  return (
    <>
      <div
        className="fixed bottom-4 right-4 z-40 max-w-sm animate-fade-up"
        data-testid="pwa-install-toast"
      >
        <div className="pro-card p-4 flex items-start gap-3 shadow-lg">
          <div className="h-10 w-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center flex-shrink-0">
            <Smartphone className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-heading font-bold text-sm">Install the app</div>
            <p className="text-xs text-muted-foreground mt-0.5">
              One-tap access from your home screen — desktop or mobile.
            </p>
            <div className="flex gap-2 mt-3">
              <Button
                size="sm"
                onClick={install}
                className="rounded-full font-semibold h-8 px-3"
                data-testid="pwa-install-btn"
              >
                <Download className="w-3.5 h-3.5 mr-1.5" /> Install
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={dismiss}
                className="rounded-full h-8 text-muted-foreground"
                data-testid="pwa-install-dismiss"
              >
                Not now
              </Button>
            </div>
          </div>
          <button
            onClick={dismiss}
            className="text-muted-foreground hover:text-foreground"
            aria-label="Dismiss"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {showIosSheet && (
        <div
          className="fixed inset-0 z-50 bg-foreground/40 backdrop-blur-sm flex items-end sm:items-center justify-center p-4"
          onClick={() => setShowIosSheet(false)}
          data-testid="ios-install-sheet"
        >
          <div
            className="pro-card p-6 w-full max-w-md"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start gap-3 mb-3">
              <div className="h-10 w-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
                <Smartphone className="w-5 h-5" />
              </div>
              <div>
                <div className="font-heading text-lg font-bold">Add to home screen</div>
                <p className="text-sm text-muted-foreground">iPhone / iPad — two quick taps.</p>
              </div>
            </div>
            <ol className="space-y-3 text-sm">
              <li className="flex gap-3">
                <span className="h-6 w-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold text-xs flex-shrink-0">1</span>
                Tap the <strong>Share</strong> icon in Safari&apos;s bottom bar.
              </li>
              <li className="flex gap-3">
                <span className="h-6 w-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold text-xs flex-shrink-0">2</span>
                Choose <strong>&quot;Add to Home Screen&quot;</strong>, then tap <strong>Add</strong>.
              </li>
            </ol>
            <Button className="w-full mt-6 rounded-full" onClick={() => setShowIosSheet(false)}>
              Got it
            </Button>
          </div>
        </div>
      )}
    </>
  );
};

export default PWAInstallButton;
